import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Plus, House, EnvelopeSimple, Phone, X, ArrowRight, Sparkle, TrashSimple, PaperPlaneTilt } from "@phosphor-icons/react";

const STATUSES = [
  { key: "new",       label: "New Inquiry", color: "bg-sky-100 border-sky-400 text-sky-900" },
  { key: "contacted", label: "Contacted",   color: "bg-indigo-100 border-indigo-400 text-indigo-900" },
  { key: "toured",    label: "Toured",      color: "bg-violet-100 border-violet-400 text-violet-900" },
  { key: "applied",   label: "Applied",     color: "bg-amber-100 border-amber-400 text-amber-900" },
  { key: "approved",  label: "Approved",    color: "bg-emerald-100 border-emerald-400 text-emerald-900" },
  { key: "signed",    label: "Signed 🎉",   color: "bg-green-500 border-green-700 text-white" },
  { key: "lost",      label: "Lost",        color: "bg-slate-100 border-slate-300 text-slate-500" },
];

export default function ProspectRadar() {
  const [prospects, setProspects] = useState(null);
  const [counts, setCounts] = useState({});
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/prospects");
      setProspects(r.data.prospects || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Prospect Radar requires the Management module — upgrade in Billing.");
      }
      setProspects([]);
    }
  };
  useEffect(() => { load(); }, []);

  const columns = useMemo(() => {
    const byStatus = {};
    STATUSES.forEach(s => byStatus[s.key] = []);
    (prospects || []).forEach(p => { (byStatus[p.status] || byStatus.new).push(p); });
    return byStatus;
  }, [prospects]);

  const moveStatus = async (p, newStatus) => {
    if (p.status === newStatus) return;
    setBusy(true);
    try {
      await api.patch(`/prospects/${p.id}`, { status: newStatus });
      toast.success(`Moved ${p.name || "prospect"} → ${STATUSES.find(s => s.key === newStatus)?.label || newStatus}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete prospect ${p.name || p.email || p.phone || "(unnamed)"}? This can't be undone.`)) return;
    try { await api.delete(`/prospects/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const sendApp = async (p) => {
    if (!p.email) { toast.error("Prospect needs an email to receive the application"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/prospects/${p.id}/send-application`);
      toast.success(`Application emailed to ${p.email}`);
      // Copy the apply URL to clipboard too for easy manual sharing
      try { await navigator.clipboard?.writeText(data.apply_url); } catch {}
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send application");
    } finally { setBusy(false); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-8 max-w-[1600px] mx-auto" data-testid="prospect-radar-page">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-gradient-to-br from-fuchsia-500 to-violet-600 flex items-center justify-center border-2 border-[#0f172a]">
                <House weight="fill" size={22} className="text-white"/>
              </div>
              <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
                Prospect Radar™
              </h1>
            </div>
            <p className="mt-1 text-sm text-slate-600 max-w-2xl">
              Track leasing inquiries from Zillow, Realtor, ShowMojo — or add walk-ins manually. New emails from listing sites land here automatically once your inbox pipeline is wired.
            </p>
          </div>
          <button onClick={() => setCreating(true)} data-testid="prospect-new-btn"
            className="tactile-btn h-11 px-5 bg-[#00C4CC] text-white font-black uppercase tracking-widest text-xs inline-flex items-center gap-2">
            <Plus weight="bold" size={14}/> New Prospect
          </button>
        </div>

        {prospects === null && (
          <div className="mt-8 text-center font-black uppercase tracking-widest text-slate-400">Loading…</div>
        )}

        {prospects && prospects.length === 0 && !creating && (
          <div className="mt-10 rugged-card p-8 text-center bg-white" data-testid="prospect-empty">
            <Sparkle weight="fill" size={40} className="mx-auto text-fuchsia-500"/>
            <div className="mt-2 font-display font-black text-xl uppercase tracking-tight">No prospects yet</div>
            <p className="mt-1 text-sm text-slate-600 max-w-md mx-auto">
              Wire your Zillow / Realtor forwarding rules into your AppFolio Inbox URL and Field Radar auto-creates prospect cards as inquiries land. Or add one manually to test the flow.
            </p>
            <button onClick={() => setCreating(true)}
              className="mt-4 tactile-btn h-10 px-5 bg-[#0f172a] text-white font-black uppercase tracking-widest text-xs">
              + Add First Prospect
            </button>
          </div>
        )}

        {prospects && prospects.length > 0 && (
          <div className="mt-6 grid gap-4" style={{ gridTemplateColumns: "repeat(7, minmax(200px, 1fr))" }}>
            {STATUSES.map(s => (
              <div key={s.key} data-testid={`prospect-col-${s.key}`} className="min-w-0">
                <div className={`p-2 border-2 ${s.color} font-black uppercase text-[11px] tracking-widest text-center`}>
                  {s.label} <span className="opacity-70">· {counts[s.key] || 0}</span>
                </div>
                <div className="mt-2 space-y-2 min-h-[80px]">
                  {(columns[s.key] || []).map(p => (
                    <ProspectCard key={p.id} p={p} busy={busy} onMove={moveStatus} onEdit={() => setEditing(p)} onDelete={() => del(p)} onSendApp={sendApp}/>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {creating && <ProspectForm onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }}/>}
        {editing && <ProspectForm prospect={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
      </div>
    </AdminShell>
  );
}

function ProspectCard({ p, busy, onMove, onEdit, onDelete, onSendApp }) {
  const currentIdx = STATUSES.findIndex(s => s.key === p.status);
  const nextStatus = STATUSES[Math.min(currentIdx + 1, STATUSES.length - 2)]?.key; // don't advance to "lost" automatically
  return (
    <div data-testid={`prospect-card-${p.id}`} className="p-3 bg-white border-2 border-[#0f172a] shadow-[3px_3px_0px_#0f172a] hover:shadow-[5px_5px_0px_#00C4CC] transition-shadow">
      <button onClick={onEdit} className="w-full text-left">
        <div className="font-black text-sm truncate">{p.name || "(no name)"}</div>
        {p.property_address && (
          <div className="text-[11px] font-bold text-slate-600 truncate">
            <House weight="bold" size={10} className="inline mr-1"/>{p.property_address}{p.unit ? ` · ${p.unit}` : ""}
          </div>
        )}
        {p.email && (
          <div className="text-[10px] text-slate-500 truncate mt-0.5">
            <EnvelopeSimple weight="bold" size={9} className="inline mr-1"/>{p.email}
          </div>
        )}
        {p.phone && (
          <div className="text-[10px] text-slate-500 truncate">
            <Phone weight="bold" size={9} className="inline mr-1"/>{p.phone}
          </div>
        )}
        {p.notes && <div className="mt-1 text-[10px] italic text-slate-500 line-clamp-2">{p.notes}</div>}
        {p.source && p.source !== "manual" && (
          <div className="mt-1 inline-block px-1.5 py-0.5 bg-fuchsia-100 border border-fuchsia-400 text-[9px] font-black uppercase tracking-widest text-fuchsia-900">
            {p.source}
          </div>
        )}
      </button>
      {nextStatus && p.status !== "signed" && p.status !== "lost" && (
        <div className="mt-2 flex items-center gap-1">
          <button onClick={() => onMove(p, nextStatus)} disabled={busy} data-testid={`prospect-advance-${p.id}`}
            className="flex-1 h-7 text-[9px] font-black uppercase tracking-widest bg-[#00C4CC] text-white hover:opacity-90 inline-flex items-center justify-center gap-1">
            Advance <ArrowRight size={9} weight="bold"/>
          </button>
          <button onClick={() => onMove(p, "lost")} disabled={busy} data-testid={`prospect-lost-${p.id}`}
            className="h-7 px-2 text-[9px] font-black uppercase tracking-widest bg-slate-200 hover:bg-slate-300 text-slate-700">
            Lost
          </button>
          <button onClick={onDelete} title="Delete" data-testid={`prospect-delete-${p.id}`}
            className="h-7 w-7 text-[9px] font-black uppercase text-slate-400 hover:text-red-600 inline-flex items-center justify-center">
            <TrashSimple size={11} weight="bold"/>
          </button>
        </div>
      )}
      {p.email && p.status !== "signed" && p.status !== "lost" && (
        <button onClick={(e) => { e.stopPropagation(); onSendApp(p); }} disabled={busy} data-testid={`prospect-send-app-${p.id}`}
          className="mt-1 w-full h-7 text-[9px] font-black uppercase tracking-widest bg-fuchsia-600 hover:bg-fuchsia-700 text-white inline-flex items-center justify-center gap-1">
          <PaperPlaneTilt size={9} weight="fill"/> Send Application
        </button>
      )}
    </div>
  );
}

function ProspectForm({ prospect, onClose, onSaved }) {
  const [f, setF] = useState({
    name: prospect?.name || "", email: prospect?.email || "", phone: prospect?.phone || "",
    property_address: prospect?.property_address || "", unit: prospect?.unit || "",
    source: prospect?.source || "manual", notes: prospect?.notes || "",
    move_in_date: prospect?.move_in_date || "", budget: prospect?.budget || "",
    status: prospect?.status || "new",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.name && !f.email && !f.phone) {
      toast.error("At least one of name / email / phone is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...f, budget: f.budget ? parseInt(f.budget) : undefined, move_in_date: f.move_in_date || undefined };
      if (prospect) await api.patch(`/prospects/${prospect.id}`, payload);
      else await api.post("/prospects", payload);
      toast.success(prospect ? "Prospect updated" : "Prospect added");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-start sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-[#0f172a] shadow-[10px_10px_0px_#0f172a] w-full max-w-lg" onClick={e => e.stopPropagation()}
        data-testid="prospect-form">
        <div className="flex items-center justify-between p-4 border-b-2 border-[#0f172a]">
          <div className="font-display font-black uppercase text-lg">{prospect ? "Edit Prospect" : "Add Prospect"}</div>
          <button onClick={onClose} aria-label="Close" data-testid="prospect-form-close" className="p-2 hover:bg-slate-100"><X weight="bold" size={16}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name" v={f.name} onChange={upd("name")} testId="prospect-name" span/>
            <Field label="Email" v={f.email} onChange={upd("email")} testId="prospect-email"/>
            <Field label="Phone" v={f.phone} onChange={upd("phone")} testId="prospect-phone"/>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Interested Property</label>
            <AddressAutocomplete
              value={f.property_address}
              onChange={(v) => setF(prev => ({ ...prev, property_address: v }))}
              onSelect={({ label }) => setF(prev => ({ ...prev, property_address: label }))}
              testId="prospect-address"
              placeholder="Start typing…"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Unit" v={f.unit} onChange={upd("unit")} testId="prospect-unit"/>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Source</label>
              <select value={f.source} onChange={upd("source")} data-testid="prospect-source"
                className="w-full h-10 px-2 border-2 border-slate-300 text-sm font-medium">
                {["manual","zillow","realtor","trulia","apartments","rentcafe","showmojo","tenantturner","referral","walk-in","other"].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <Field label="Budget" v={f.budget} onChange={upd("budget")} testId="prospect-budget" type="number"/>
            <Field label="Move-in Date" v={f.move_in_date} onChange={upd("move_in_date")} testId="prospect-movein" type="date"/>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Notes</label>
            <textarea value={f.notes} onChange={upd("notes")} rows={3} data-testid="prospect-notes"
              className="w-full px-2 py-2 border-2 border-slate-300 text-sm font-medium resize-none"/>
          </div>
          {prospect && (
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Status</label>
              <select value={f.status} onChange={upd("status")} data-testid="prospect-status"
                className="w-full h-10 px-2 border-2 border-slate-300 text-sm font-medium">
                {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="p-4 border-t-2 border-[#0f172a] flex items-center gap-2 justify-end">
          <button onClick={onClose} data-testid="prospect-form-cancel"
            className="h-11 px-4 border-2 border-slate-300 text-slate-600 text-[11px] font-black uppercase tracking-widest hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="prospect-form-save"
            className="h-11 px-5 bg-[#00C4CC] text-white text-[11px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40">
            {saving ? "Saving…" : "Save Prospect"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, onChange, testId, span, type = "text" }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">{label}</label>
      <input type={type} value={v || ""} onChange={onChange} data-testid={testId}
        className="w-full h-10 px-2 border-2 border-slate-300 text-sm font-medium focus:border-[#0f172a] focus:outline-none"/>
    </div>
  );
}
